import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useAuth } from '../../context/AuthContext';
import { useSettings, useUpdateSettings } from '../../hooks/useStatusPages';
import * as api from '../../api/client';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { checkboxClass } from '../common/Input';
import { useToast } from '../common/Toast';
import { apiError } from '../../utils/apiError';
import { ConfigExport, ImportResult } from '../../types';
import { ImportPreview } from './ImportPreview';
import { BackupsCard, RetentionCard, ConfigFileCard, ApiDocsCard } from './OperationsCards';

export function ConfigPage() {
  useDocumentTitle('Configuration');
  const { addToast } = useToast();
  const { hasPermission } = useAuth();
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exporting, setExporting] = useState(false);
  const [includeSecrets, setIncludeSecrets] = useState(false);

  const [pending, setPending] = useState<{ name: string; data: ConfigExport; preview: ImportResult } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await api.exportConfig(includeSecrets);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = includeSecrets ? '-with-secrets' : '';
      a.download = `pulsecheck-config-${new Date().toISOString().split('T')[0]}${suffix}.json`;
      a.click();
      URL.revokeObjectURL(url);
      addToast('success', includeSecrets ? 'Configuration exported — this file contains secrets' : 'Configuration exported');
    } catch (err) {
      addToast('error', apiError(err, 'Failed to export configuration'));
    } finally {
      setExporting(false);
    }
  };

  /** Step 1: parse the file and ask the server what it would do. Nothing is written. */
  const handleChooseFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPreviewing(true);
    try {
      const text = await file.text();

      let data: unknown;
      try {
        data = JSON.parse(text);
      } catch {
        // Distinguish "your file is malformed" from "the server refused it".
        addToast('error', `${file.name} is not valid JSON.`);
        return;
      }

      const preview = await api.importConfig(data as ConfigExport, true);
      setPending({ name: file.name, data: data as ConfigExport, preview });
    } catch (err) {
      addToast('error', apiError(err, 'Could not read this file. Check it was exported by PulseCheck.'));
    } finally {
      setPreviewing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /** Step 2: apply, after the user has seen the preview. */
  const handleApply = async () => {
    if (!pending) return;
    setApplying(true);
    try {
      const result = await api.importConfig(pending.data, false);
      const total = (s: ImportResult['services']) => s.created + s.updated;
      addToast(
        'success',
        `Imported: ${total(result.services)} services, ${total(result.groups)} groups and ${total(result.notifications)} notification channels created or updated`,
      );
      const creds = result.credentials_required.services.length + result.credentials_required.notifications.length;
      if (creds > 0) {
        addToast('info', `${creds} ${creds === 1 ? 'item needs' : 'items need'} credentials re-entered — see the list above before you leave.`);
        // Keep the panel up so the credential list stays visible.
        setPending({ ...pending, preview: result });
      } else {
        setPending(null);
      }
    } catch (err) {
      addToast('error', apiError(err, 'Import failed.'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold tracking-tight">Configuration</h1>

      <Card className="p-6 space-y-4">
        <h2 className="font-semibold text-lg">Export</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Downloads every service, group and notification channel as a JSON file. By default credentials,
          webhook URLs, tokens and authorisation headers are <strong className="font-medium">left out</strong>,
          so the file is safe to keep in version control — importing it will list what needs re-entering.
        </p>
        <label className="inline-flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeSecrets}
            onChange={e => setIncludeSecrets(e.target.checked)}
            className={`${checkboxClass} mt-0.5`}
          />
          <span>
            Include secrets
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              Webhook URLs, API tokens, SMTP passwords, header values, signing secrets and service credentials in plain text. Treat the file like a password.
            </span>
          </span>
        </label>
        <Button onClick={handleExport} loading={exporting}>
          {includeSecrets ? 'Export with secrets' : 'Export configuration'}
        </Button>
      </Card>

      <Card className="p-6 space-y-4">
        <h2 className="font-semibold text-lg">Status pages</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Shareable pages showing name, status, 90-day uptime, incidents and maintenance for the services you put on
          them — one per client if you like, each with its own branding and access rule. URLs, headers and error
          messages are never exposed.
        </p>
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className={`${checkboxClass} mt-0.5`}
            checked={settings?.['status_pages.enabled'] ?? false}
            disabled={!settings || !hasPermission('config.edit') || updateSettings.isPending}
            onChange={e => updateSettings.mutate({ 'status_pages.enabled': e.target.checked }, {
              onSuccess: s => addToast('success', s['status_pages.enabled'] ? 'Status pages are live' : 'Status pages are switched off'),
              onError: err => addToast('error', apiError(err, 'Could not change the setting')),
            })}
          />
          <span>
            Serve status pages
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              Instance-wide switch, saved here — no env var or restart. Each page also has its own enabled flag.
            </span>
          </span>
        </label>
        <div className="flex gap-2 flex-wrap">
          <Link to="/status-pages"><Button variant="secondary">Manage status pages</Button></Link>
          <a href="/status" target="_blank" rel="noreferrer" className="inline-block">
            <Button variant="ghost">Open default page ↗</Button>
          </a>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h2 className="font-semibold text-lg">Import</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Items are matched by name: existing services, groups and notification channels with the same
          name are updated, new ones are created, and nothing is ever deleted. Files from before
          channels existed still import — their webhooks become webhook-type channels. You see a
          preview before anything changes.
        </p>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleChooseFile}
            className="sr-only"
            aria-label="Choose a configuration file to import"
          />
          <Button variant="secondary" onClick={() => fileInputRef.current?.click()} loading={previewing}>
            {previewing ? 'Reading…' : pending ? 'Choose a different file' : 'Choose file to preview'}
          </Button>
        </div>

        {pending && (
          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-sm">
              <span className="font-medium">{pending.name}</span>
              {pending.preview.dry_run
                ? <span className="text-gray-500 dark:text-gray-400"> — preview, nothing has been changed yet</span>
                : <span className="text-green-700 dark:text-green-400"> — applied</span>}
            </p>
            <ImportPreview result={pending.preview} />
            <div className="flex gap-3">
              {pending.preview.dry_run && (
                <Button onClick={handleApply} loading={applying}>
                  {applying ? 'Applying…' : 'Apply import'}
                </Button>
              )}
              <Button variant="secondary" onClick={() => setPending(null)}>
                {pending.preview.dry_run ? 'Cancel' : 'Done'}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ConfigFileCard />
      <BackupsCard />
      <RetentionCard />
      <ApiDocsCard />
    </div>
  );
}
